import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { EditableImage } from './EditableImage';
import { EditableContent } from './EditableContent';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface CommitteeMember {
  id: string;
  name: string;
  role: string;
  image?: string;
  index: number;
  order: number;
}

interface CommitteeCardProps extends CommitteeMember {
  onUpdate: (id: string, field: 'name' | 'role', value: string) => Promise<void>;
}

const CommitteeCard: React.FC<CommitteeCardProps> = ({ id, name, role, image, index, onUpdate }) => {
  const [currentImage, setCurrentImage] = useState(image);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const docRef = doc(db, 'content', `committee_member-${index}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.value?.url) {
            setCurrentImage(data.value.url);
          }
        }
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };

    fetchImage();
  }, [index]);

  const handleImageUpdate = (url: string) => {
    setCurrentImage(url);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="aspect-w-3 aspect-h-4 bg-cream relative">
        <EditableImage
          section="committee"
          imageKey={`member-${index}`}
          defaultSrc={currentImage || ''}
          alt={`${name} - ${role}`}
          className="w-full h-full object-cover"
          onImageUpdate={handleImageUpdate}
        />
      </div>
      <div className="p-6">
        <h3 className="text-xl font-serif font-bold text-dark mb-2">
          <EditableContent
            section="committee"
            contentKey={`member-${index}-name`}
            defaultValue={name}
            onSave={async (value) => await onUpdate(id, 'name', value)}
          />
        </h3>
        <p className="text-dark/80 text-sm">
          <EditableContent
            section="committee"
            contentKey={`member-${index}-role`}
            defaultValue={role}
            onSave={async (value) => await onUpdate(id, 'role', value)}
          />
        </p>
      </div>
    </div>
  );
};

export default CommitteeCard;